import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { fetchDateFormatFromBusiness, getDateFormat, setDateFormat as setDateFormatUtil, setGlobalDateFormatRef, DEFAULT_DATE_FORMAT } from "../config/dateFormat";
import { fetchCurrencyFromBusiness } from "../config/currencyFormat";

const DateFormatContext = createContext();

// Global variable to store current format (for formatDate function to access)
let globalDateFormat = getDateFormat();

export function DateFormatProvider({ children }) {
  const [dateFormat, setDateFormatState] = useState(() => {
    // Initialize from sessionStorage if available
    const format = getDateFormat();
    globalDateFormat = format;
    return format;
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch date format on mount
  useEffect(() => {
    const loadDateFormat = async () => {
      setIsLoading(true);
      try {
        const userRole = sessionStorage.getItem("user_role");
        const userId = sessionStorage.getItem("user_id");
        
        if (userRole?.toLowerCase() === "vendor" && userId) {
          // Vendor currency rides along with the date format load (same business details)
          fetchCurrencyFromBusiness();
          const format = await fetchDateFormatFromBusiness();
          console.log("DateFormatContext: Loaded format from API:", format);
          globalDateFormat = format;
          setGlobalDateFormatRef(format);
          setDateFormatState(format);
          // Ensure sessionStorage is also updated
          if (format && format !== DEFAULT_DATE_FORMAT) {
            sessionStorage.setItem("business_date_format", format);
          }
        } else {
          // Use default if not vendor
          const currentFormat = getDateFormat();
          globalDateFormat = currentFormat;
          setGlobalDateFormatRef(currentFormat);
          setDateFormatState(currentFormat);
        }
      } catch (error) {
        console.error("Error loading date format:", error);
        // Use default on error
        const currentFormat = getDateFormat();
        globalDateFormat = currentFormat;
        setGlobalDateFormatRef(currentFormat);
        setDateFormatState(currentFormat);
      } finally {
        setIsLoading(false);
      }
    };

    loadDateFormat();
  }, []);

  // Listen for storage changes (when format is updated in Business Details)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "business_date_format" || e.key === null) {
        const newFormat = getDateFormat();
        globalDateFormat = newFormat;
        setGlobalDateFormatRef(newFormat);
        setDateFormatState(newFormat);
      }
    };

    // Listen to storage events (for cross-tab updates)
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom event when date format is updated in same tab
    const handleDateFormatUpdate = () => {
      const newFormat = getDateFormat();
      globalDateFormat = newFormat;
      setGlobalDateFormatRef(newFormat);
      setDateFormatState(newFormat);
    };

    window.addEventListener("dateFormatUpdated", handleDateFormatUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("dateFormatUpdated", handleDateFormatUpdate);
    };
  }, []);

  const updateDateFormat = useCallback((format) => {
    if (format) {
      console.log("DateFormatContext: Updating format to", format);
      setDateFormatUtil(format); // This updates sessionStorage
      globalDateFormat = format;
      setGlobalDateFormatRef(format);
      setDateFormatState(format);
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent("dateFormatUpdated"));
      console.log("DateFormatContext: Format updated successfully");
    }
  }, []);

  // Provide formatDate function that uses context state
  const formatDate = useCallback((date) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      return "";
    }

    const format = dateFormat; // Use context state directly
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const monthName = dateObj.toLocaleString("en-US", { month: "long" });
    const monthShort = dateObj.toLocaleString("en-US", { month: "short" });

    switch (format) {
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      case "MM/DD/YYYY":
        return `${month}/${day}/${year}`;
      case "DD/MM/YYYY":
        return `${day}/${month}/${year}`;
      case "DD-MM-YYYY":
        return `${day}-${month}-${year}`;
      case "MM-DD-YYYY":
        return `${month}-${day}-${year}`;
      case "YYYY/MM/DD":
        return `${year}/${month}/${day}`;
      case "DD.MM.YYYY":
        return `${day}.${month}.${year}`;
      case "MMM DD, YYYY":
        return `${monthShort} ${day}, ${year}`;
      case "MMMM DD, YYYY":
        return `${monthName} ${day}, ${year}`;
      default:
        return `${year}-${month}-${day}`; // Default to YYYY-MM-DD
    }
  }, [dateFormat]);

  return (
    <DateFormatContext.Provider value={{ dateFormat, isLoading, updateDateFormat, formatDate }}>
      {children}
    </DateFormatContext.Provider>
  );
}

export function useDateFormat() {
  const context = useContext(DateFormatContext);
  if (context === undefined) {
    throw new Error("useDateFormat must be used within a DateFormatProvider");
  }
  return context;
}

